import type { Workspace } from '@stratumn/studio-shared';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WorkspaceFormValues } from '../form/form.workspace';
import { WorkspaceForm } from '../form/form.workspace';
import { VersionBadge } from './VersionBadge';

const getEditDefaults = (ws: Workspace): WorkspaceFormValues => ({
  name: ws.name,
  color: ws.color || '#6366f1',
  version: ws.version ?? null
});

type Props = {
  workspace: Workspace;
  processId: string;
  isEditing: boolean;
  onToggleEdit: () => void;
  onUpdate: (values: WorkspaceFormValues) => void | Promise<void>;
  onCancelEdit: () => void;
  onRequestDelete: (workspace: Workspace) => void;
};

export const WorkspaceCard = ({
  workspace,
  processId,
  isEditing,
  onToggleEdit,
  onUpdate,
  onCancelEdit,
  onRequestDelete
}: Props) => {
  const shouldReduceMotion = useReducedMotion();
  const navigate = useNavigate();

  const handleRowClick = () => {
    if (!isEditing) {
      navigate(`/${processId}/workspaces/${workspace.id}`);
    }
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg bg-white shadow-verail transition-shadow duration-150 hover:shadow-verailHover"
      exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
      initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
      layout={!shouldReduceMotion}
      transition={{
        type: 'spring',
        duration: 0.3,
        bounce: 0,
        layout: { type: 'spring', duration: 0.3, bounce: 0 }
      }}
    >
      <div
        className={cn(
          'flex items-center justify-between p-4',
          !isEditing && 'cursor-pointer'
        )}
        onClick={handleRowClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleRowClick();
          }
        }}
        role={isEditing ? undefined : 'button'}
        tabIndex={isEditing ? undefined : 0}
      >
        <div className="flex items-center gap-3">
          <div
            className="h-4 w-4 shrink-0 rounded-full"
            style={{ backgroundColor: workspace.color || '#6366f1' }}
          />
          <span className="font-medium text-sm">{workspace.name}</span>
          <VersionBadge version={workspace.version} />
        </div>
        <div className="flex items-center gap-1">
          <Button
            aria-label={
              isEditing
                ? 'Collapse edit form'
                : `Edit workspace ${workspace.name}`
            }
            onClick={e => {
              e.stopPropagation();
              onToggleEdit();
            }}
            size="icon"
            variant="ghost"
          >
            <AnimatePresence initial={false} mode="popLayout">
              {isEditing ? (
                <motion.span
                  animate={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  exit={{ filter: 'blur(2px)', opacity: 0, scale: 0.5 }}
                  initial={{ filter: 'blur(2px)', opacity: 0, scale: 0.5 }}
                  key="chevron"
                  transition={{ type: 'spring', duration: 0.15, bounce: 0 }}
                >
                  <ChevronUp className="h-4 w-4" />
                </motion.span>
              ) : (
                <motion.span
                  animate={{ filter: 'blur(0px)', opacity: 1, scale: 1 }}
                  exit={{ filter: 'blur(2px)', opacity: 0, scale: 0.5 }}
                  initial={{ filter: 'blur(2px)', opacity: 0, scale: 0.5 }}
                  key="pencil"
                  transition={{ type: 'spring', duration: 0.15, bounce: 0 }}
                >
                  <Pencil className="h-4 w-4" />
                </motion.span>
              )}
            </AnimatePresence>
          </Button>
          <Button
            aria-label={`Delete workspace ${workspace.name}`}
            onClick={e => {
              e.stopPropagation();
              onRequestDelete(workspace);
            }}
            size="icon"
            variant="ghost"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Inline edit */}
      <AnimatePresence initial={false}>
        {isEditing && (
          <motion.div
            animate={{ height: 'auto', opacity: 1 }}
            className="overflow-hidden border-t"
            exit={{ height: 0, opacity: 0 }}
            initial={shouldReduceMotion ? false : { height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          >
            <div className="p-6">
              <WorkspaceForm
                defaultValues={getEditDefaults(workspace)}
                onCancel={onCancelEdit}
                onSubmit={onUpdate}
                processId={processId}
                submitLabel="Save"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
